import { clinicDayKey } from '@/utils/calendarClinicTime';

/**
 * Interval packing for timed calendar blocks in a single practitioner column.
 *
 * Appointments that overlap in time share the column horizontally. Appointments
 * that only touch at an endpoint (13:00–14:00 and 14:00–15:00) do not overlap
 * and keep the full width. Layout is computed per practitioner/day by the
 * caller — this module never creates extra practitioner columns.
 */

export const MAX_VISIBLE_OVERLAP_COLUMNS = 3;
export const OVERLAP_EDGE_PX = 4;
export const OVERFLOW_GUTTER_PX = 22;

export type TimedInterval = {
  id: string;
  startMs: number;
  endMs: number;
};

export type OverlapPlacement = {
  id: string;
  column: number;
  columnSpan: number;
  columnCount: number;
  leftPct: number;
  widthPct: number;
  hidden: boolean;
  clusterHasOverflow: boolean;
};

export type OverlapOverflowGroup = {
  ids: string[];
  startMs: number;
  endMs: number;
};

export type OverlapLayoutResult = {
  placements: Map<string, OverlapPlacement>;
  overflowGroups: OverlapOverflowGroup[];
  hasOverflow: boolean;
};

export function appointmentToInterval(appointment: {
  id: string;
  starts_at: string;
  ends_at: string;
}): TimedInterval {
  return {
    id: appointment.id,
    startMs: new Date(appointment.starts_at).getTime(),
    endMs: new Date(appointment.ends_at).getTime(),
  };
}

export function calendarDayKey(value: Date): string {
  return clinicDayKey(value);
}

export function appointmentsOnCalendarDay<T extends { starts_at: string }>(
  appointments: T[],
  day: Date,
): T[] {
  const key = calendarDayKey(day);
  return appointments.filter(
    (appointment) => calendarDayKey(new Date(appointment.starts_at)) === key,
  );
}

/** Same Europe/Copenhagen day key for column dates and the events inside them. */
export function groupByCalendarDay<T extends { starts_at: string }>(
  items: readonly T[],
): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const key = calendarDayKey(new Date(item.starts_at));
    const list = map.get(key);
    if (list) list.push(item);
    else map.set(key, [item]);
  }
  return map;
}

export function appointmentsInStaffColumn<T extends { clinician_id: string | null }>(
  appointments: T[],
  staffId?: string,
): T[] {
  if (!staffId) return appointments;
  if (staffId === 'unassigned')
    return appointments.filter((appointment) => !appointment.clinician_id);
  return appointments.filter((appointment) => appointment.clinician_id === staffId);
}

export function appointmentsInResourceColumn<T extends { id: string }>(
  appointments: T[],
  resourceId: string,
  allocationsByAppointment: Map<string, string[]>,
): T[] {
  return appointments.filter((appointment) =>
    (allocationsByAppointment.get(appointment.id) ?? []).includes(resourceId),
  );
}

export function intervalsOverlap(a: TimedInterval, b: TimedInterval): boolean {
  return a.startMs < b.endMs && b.startMs < a.endMs;
}

function isValidInterval(item: TimedInterval): boolean {
  return Number.isFinite(item.startMs) && Number.isFinite(item.endMs) && item.endMs > item.startMs;
}

function compareIntervals(a: TimedInterval, b: TimedInterval): number {
  if (a.startMs !== b.startMs) return a.startMs - b.startMs;
  const durationA = a.endMs - a.startMs;
  const durationB = b.endMs - b.startMs;
  if (durationA !== durationB) return durationB - durationA;
  return a.id.localeCompare(b.id);
}

function sortIntervals(items: TimedInterval[]): TimedInterval[] {
  return [...items].sort(compareIntervals);
}

/** Connected components of the interval overlap graph, via a start-time sweep. */
export function overlapClusters(items: TimedInterval[]): TimedInterval[][] {
  const sorted = sortIntervals(items.filter(isValidInterval));
  const clusters: TimedInterval[][] = [];
  let current: TimedInterval[] = [];
  let clusterEnd = Number.NEGATIVE_INFINITY;

  for (const item of sorted) {
    if (current.length > 0 && item.startMs >= clusterEnd) {
      clusters.push(current);
      current = [];
      clusterEnd = Number.NEGATIVE_INFINITY;
    }
    current.push(item);
    clusterEnd = Math.max(clusterEnd, item.endMs);
  }
  if (current.length > 0) clusters.push(current);
  return clusters;
}

type ColumnAssignment = {
  columnById: Map<string, number>;
  columnCount: number;
};

/**
 * Greedy leftmost column packing. An appointment reuses the first column whose
 * previous occupant has already ended (touching endpoints are allowed).
 */
function assignColumns(cluster: TimedInterval[]): ColumnAssignment {
  const columnEndMs: number[] = [];
  const columnById = new Map<string, number>();

  for (const item of sortIntervals(cluster)) {
    let column = columnEndMs.findIndex((endMs) => endMs <= item.startMs);
    if (column === -1) {
      column = columnEndMs.length;
      columnEndMs.push(item.endMs);
    } else {
      columnEndMs[column] = item.endMs;
    }
    columnById.set(item.id, column);
  }

  return { columnById, columnCount: Math.max(1, columnEndMs.length) };
}

function columnOccupiedDuring(
  cluster: TimedInterval[],
  columnById: Map<string, number>,
  column: number,
  startMs: number,
  endMs: number,
  exceptId: string,
): boolean {
  for (const item of cluster) {
    if (item.id === exceptId) continue;
    if (columnById.get(item.id) !== column) continue;
    if (item.startMs < endMs && startMs < item.endMs) return true;
  }
  return false;
}

/**
 * Grow right into columns that stay free for the appointment's full duration.
 * Stops the appointment staying 1/N wide when later columns are unused.
 */
function columnSpanFor(
  item: TimedInterval,
  column: number,
  columnCount: number,
  cluster: TimedInterval[],
  columnById: Map<string, number>,
): number {
  let span = 1;
  while (column + span < columnCount) {
    if (
      columnOccupiedDuring(cluster, columnById, column + span, item.startMs, item.endMs, item.id)
    ) {
      break;
    }
    span += 1;
  }
  return span;
}

function placementFor(
  item: TimedInterval,
  column: number,
  columnSpan: number,
  visibleColumnCount: number,
  hidden: boolean,
  clusterHasOverflow: boolean,
): OverlapPlacement {
  const count = Math.max(1, visibleColumnCount);
  const span = hidden ? 0 : Math.min(columnSpan, count - column);
  return {
    id: item.id,
    column,
    columnSpan: span,
    columnCount: count,
    leftPct: hidden ? 0 : (column / count) * 100,
    widthPct: hidden ? 0 : (span / count) * 100,
    hidden,
    clusterHasOverflow,
  };
}

function overflowGroupsFromHidden(hiddenItems: TimedInterval[]): OverlapOverflowGroup[] {
  return overlapClusters(hiddenItems).map((group) => ({
    ids: group.map((item) => item.id),
    startMs: Math.min(...group.map((item) => item.startMs)),
    endMs: Math.max(...group.map((item) => item.endMs)),
  }));
}

export function layoutOverlappingAppointments(
  items: TimedInterval[],
  options?: { maxVisibleColumns?: number },
): OverlapLayoutResult {
  const maxVisible = options?.maxVisibleColumns ?? MAX_VISIBLE_OVERLAP_COLUMNS;
  const placements = new Map<string, OverlapPlacement>();
  const overflowGroups: OverlapOverflowGroup[] = [];

  for (const item of items) {
    if (!isValidInterval(item)) {
      placements.set(item.id, {
        id: item.id,
        column: 0,
        columnSpan: 1,
        columnCount: 1,
        leftPct: 0,
        widthPct: 100,
        hidden: false,
        clusterHasOverflow: false,
      });
    }
  }

  for (const cluster of overlapClusters(items)) {
    const { columnById, columnCount } = assignColumns(cluster);
    const clusterHasOverflow = columnCount > maxVisible;
    const visibleColumnCount = clusterHasOverflow ? maxVisible : columnCount;
    const hiddenItems: TimedInterval[] = [];

    for (const item of cluster) {
      const column = columnById.get(item.id) ?? 0;
      const hidden = column >= visibleColumnCount;
      const rawSpan = columnSpanFor(item, column, columnCount, cluster, columnById);
      const span = hidden ? 0 : Math.min(rawSpan, visibleColumnCount - column);
      placements.set(
        item.id,
        placementFor(item, column, span, visibleColumnCount, hidden, clusterHasOverflow),
      );
      if (hidden) hiddenItems.push(item);
    }

    overflowGroups.push(...overflowGroupsFromHidden(hiddenItems));
  }

  return {
    placements,
    overflowGroups,
    hasOverflow: overflowGroups.length > 0,
  };
}

export function overlapBoxStyle(
  placement: OverlapPlacement,
  options?: { overflowGutterPx?: number },
): {
  position: 'absolute';
  left: string;
  width: string;
  right: 'auto';
  boxSizing: 'border-box';
  minWidth: number;
} {
  const gutter =
    options?.overflowGutterPx ?? (placement.clusterHasOverflow ? OVERFLOW_GUTTER_PX : 0);
  const edge = OVERLAP_EDGE_PX;
  const usable = gutter > 0 ? `(100% - ${gutter}px)` : '100%';

  return {
    position: 'absolute',
    left: `calc(${usable} * ${placement.column} / ${placement.columnCount} + ${edge}px)`,
    width: `calc(${usable} / ${placement.columnCount} * ${placement.columnSpan} - ${edge * 2}px)`,
    right: 'auto',
    boxSizing: 'border-box',
    minWidth: 0,
  };
}
